import type { EventCheckout } from '@irace/shared';
import type { CheckoutRecord } from '../db/checkout-record.ts';
import type { OutboxRecord } from '../db/outbox-record.ts';
import { COPY } from '../copy/vi.ts';

/**
 * G-014 — the pure rules behind the participant check-out screens.
 *
 * They are functions rather than logic inside `CheckoutPanel` or
 * `useCheckinFlow` for one reason: this repo has no React test harness, and
 * adding one for two screens is not a trade worth making. So the parts that
 * can be wrong on event day — which record gets offered a check-out, and what
 * text the controls carry — live here where a test can pin them, exactly as
 * `admin/publishState.ts` and `admin/checkoutConfig.ts` do on the admin side.
 */

/**
 * The check-in a returning participant should be offered a check-out for, or
 * null to open on the normal form.
 *
 * Three conditions, and each one is load-bearing:
 *
 * - **Accepted only.** A record that is still pending belongs to the pending
 *   screen (§15), which is already showing it and already retrying it. Two
 *   screens claiming one record would fight over the same state, and the one
 *   that lost would be the one telling the truth about whether the server has
 *   the check-in at all.
 * - **No check-out on the device.** Any local check-out — pending, sending,
 *   accepted, even terminally failed — means the participant already tapped
 *   the button. A pending one is the retry engine's to finish; re-offering it
 *   would invite a second `save()` that resets the attempt count and moves the
 *   recorded departure time forward.
 * - **This event.** A device that visited two events holds records for both.
 *
 * Among what survives, the most recent check-in wins: someone who checked in
 * twice is leaving the second arrival, not the first. Instants are compared,
 * not strings, because a record written by an older bundle (or a hand-written
 * config) can carry an offset rather than `Z`.
 *
 * Nothing here bounds the age of the record. It does not need to: accepted
 * check-ins are pruned after six hours (§17) and these events run two to
 * three, so a participant who returns the next day simply sees the form.
 */
export function resumableCheckin(
  records: OutboxRecord[],
  checkouts: CheckoutRecord[],
  eventId: string,
): OutboxRecord | null {
  const checkedOut = new Set(checkouts.map((checkout) => checkout.submissionId));

  let best: OutboxRecord | null = null;
  let bestAt = Number.NEGATIVE_INFINITY;

  for (const record of records) {
    if (record.eventId !== eventId) continue;
    if (record.status !== 'accepted') continue;
    if (checkedOut.has(record.submissionId)) continue;

    // An unparseable timestamp must not win the comparison, but it must still
    // be offerable when it is the only candidate — the check-in happened.
    const at = Date.parse(record.clientCreatedAt);
    const rank = Number.isNaN(at) ? Number.NEGATIVE_INFINITY : at;
    if (best !== null && rank <= bestAt) continue;

    best = record;
    bestAt = rank;
  }

  return best;
}

/**
 * `FlowState['kind']`, restated rather than imported.
 *
 * Importing the type would drag `useCheckinFlow` — and through it Turnstile
 * and the DOM — into this module's graph, and this module is imported by a
 * test project that compiles without `lib.dom`. Restating it costs nothing in
 * drift: a kind added to the state machine and not added here fails to compile
 * at the call site in `CheckinExperience`, which is where someone would have
 * to decide what the new state means for the resume screen anyway.
 */
export type FlowStateKind =
  'form' | 'submitting' | 'pending' | 'verify_retry' | 'accepted' | 'rejected' | 'save_failed';

/**
 * Whether the resume screen may take the screen right now.
 *
 * Separate from `resumableCheckin` because it answers a different question.
 * That one asks "is there a check-in worth offering a check-out for", from
 * data on disk. This one asks "may we replace what is currently on screen",
 * and the honest answer depends on what the participant is doing.
 *
 * The reason it exists is a real sequence, not a hypothetical: the stores are
 * read asynchronously, and the read is slowest in exactly the situation this
 * feature already worries about — a stale tab blocking the database. So the
 * candidate can arrive seconds after the form did, while the next person at
 * the desk is halfway through typing their name. `CheckinForm` keeps its
 * values in local state, so swapping it out unmounts it and everything typed
 * is gone, with a queue waiting behind them.
 *
 * Hence `formDirty` wins over any candidate, and it is one-way for the life of
 * the visit: a form that was touched keeps the screen until the participant
 * leaves it themselves. "Hold, but never while touched" rather than "hold and
 * pop later" because a screen that can still appear later is the same bug with
 * a longer fuse — there is no moment where replacing a form someone worked on
 * is a kindness.
 *
 * `stateKind` is the second half: a submission the participant is waiting on
 * owns the screen (§15, §18), and only `form` is idle enough to be replaced.
 */
export function resumeTakesOver(input: {
  candidate: OutboxRecord | null;
  formDirty: boolean;
  stateKind: FlowStateKind;
}): boolean {
  if (!input.candidate) return false;
  if (input.formDirty) return false;
  return input.stateKind === 'form';
}

/**
 * The check-out config to build and render against, or null for neither.
 *
 * One function so the engine and the panel cannot gate on different
 * conditions. The API strips a disabled config out of the bootstrap today, so
 * `checkout` present but `enabled: false` cannot currently reach a device —
 * but the schema permits it, and if that exposure ever changed, a screen
 * gating on mere presence would render a button whose every tap lands in a
 * permanent failure: no store, no service, and nothing to retry.
 */
export function enabledCheckout(checkout: EventCheckout | undefined): EventCheckout | null {
  return checkout?.enabled === true ? checkout : null;
}

export interface CheckoutLabels {
  notice: string;
  button: string;
  keepOpenHint: string;
}

/**
 * The three strings the panel renders, organizer wording first.
 *
 * Per string, not per block: an organizer who renamed the button and left the
 * notice alone gets their button and the default notice, rather than a screen
 * that silently drops the sentence explaining why the button is there.
 *
 * Blank counts as absent. `toCheckoutPatch` already drops empty fields on the
 * way in, so this is the second line of defence for a snapshot published
 * before that rule existed — and the failure it prevents is the bad one: a
 * button with no label, which nobody at the venue can guess the purpose of.
 */
export function checkoutLabels(checkout: EventCheckout): CheckoutLabels {
  return {
    notice: text(checkout.notice) ?? COPY.checkout.notice,
    button: text(checkout.button_label) ?? COPY.checkout.button,
    keepOpenHint: text(checkout.keep_open_hint) ?? COPY.checkout.keepOpenHint,
  };
}

function text(value: string | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}
